import { Alert, Box, Button, Divider, Link, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { type ReactNode, useEffect, useState } from 'react'
import { Navigate, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../../hooks/use-auth'
import { useP2PInviteRedemption } from '../../../../hooks/use-p2pInviteRedemption'
import { AppRoutes } from '../../../Routes'
import { GoogleIcon } from '../../../../assets/svgs/GoogleIcon'
import { MicrosoftIcon } from '../../../../assets/svgs/MicrosoftIcon'
import { P2PAuthShell } from './P2PAuthShell'
import { InviteRedemptionNotice } from './InviteRedemptionNotice'
import { captureInviteToken } from './inviteToken'
import { styles } from './P2PAuthLanding.styles'

/**
 * START-owned "Start Here" landing for invited Peer-to-Peer users. Every sign-in option is a
 * redirect: MiDataHub through MiLaunchPad, Google and Microsoft straight to that provider via
 * MiCredentials, and MiCredentials to its own hosted login page (which also owns password
 * reset). Invitees with no account go to Create Account. An emailed link's invite code is kept
 * across any of these and redeemed once the invitee is signed in.
 */
export const P2PAuthLanding = (): JSX.Element => {
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  // Captured during the first render, before the redemption hook's effect reads it.
  const [arrivedWithInvite] = useState(() => captureInviteToken(location.search))
  const redemption = useP2PInviteRedemption()
  const returnUrl = AppRoutes.PEER_TO_PEER_START

  useEffect(() => {
    if (arrivedWithInvite) navigate(AppRoutes.PEER_TO_PEER_START, { replace: true })
  }, [arrivedWithInvite])

  if (redemption.status === 'waiting') return <></>

  const hasAccount = auth.user != null && !auth.user.noAccount
  if (redemption.status === 'accepted' || (redemption.status === 'none' && hasAccount)) {
    return <Navigate to={AppRoutes.PEER_TO_PEER} replace />
  }

  const providers: Array<{ key: string, label: string, icon?: ReactNode, onClick: () => void }> = [
    { key: 'midatahub', label: 'Access with MiDataHub', onClick: () => { auth.miLaunchPadLogin(returnUrl) } },
    { key: 'google', label: 'Access with Google', icon: <GoogleIcon />, onClick: () => { auth.miCredsLogin(returnUrl, 'Google') } },
    { key: 'microsoft', label: 'Access with Microsoft', icon: <MicrosoftIcon />, onClick: () => { auth.miCredsLogin(returnUrl, 'Microsoft') } }
  ]

  return (
    <P2PAuthShell>
      <Box data-testid='p2pAuthLanding'>
        <Typography variant='h1' sx={styles.title}>Start Here</Typography>
        <Typography variant='body1' sx={styles.subtitle}>Access your form using one of the options below.</Typography>

        {redemption.status === 'failed' && (
          <InviteRedemptionNotice result={redemption.result} signedInEmail={auth.user?.email ?? null} onSignOut={auth.logout} />
        )}
        {redemption.status === 'none' && auth.user?.noAccount === true && (
          <Alert severity='info' sx={styles.notice} data-testid='p2pNoInvitationNotice'>
            You're signed in as {auth.user.email}, but there's no Peer to Peer invitation for that address. Sign in with the
            email address your invitation was sent to.
          </Alert>
        )}

        <Box sx={styles.providerList}>
          {providers.map(provider => (
            <Button
              key={provider.key}
              variant='outlined'
              fullWidth
              startIcon={provider.icon}
              onClick={provider.onClick}
              data-testid={`p2pAuthProvider-${provider.key}`}
              sx={styles.providerButton}
            >
              {provider.label}
            </Button>
          ))}
        </Box>

        <Divider sx={{ my: '20px' }}>or</Divider>

        <Typography variant='h4' sx={styles.micredsHeading}>Sign in with your MiCredentials account</Typography>
        <Button
          variant='contained'
          color='primary'
          fullWidth
          endIcon={<ArrowForwardIcon />}
          onClick={() => { auth.miCredsLogin(returnUrl) }}
          data-testid='p2pAuthProvider-micredentials'
          sx={styles.micredsButton}
        >
          Get Started
        </Button>

        <Typography variant='body1' sx={styles.createAccount}>
          Don't have an account yet?{' '}
          <Link component={RouterLink} to={AppRoutes.PEER_TO_PEER_CREATE_ACCOUNT} data-testid='p2pAuthCreateAccount'>
            Create Account
          </Link>
        </Typography>

        <Typography variant='caption' color='text.secondary' sx={styles.footnote}>
          Sign-in and password reset are handled by MiCredentials. START never stores your password.
        </Typography>
      </Box>
    </P2PAuthShell>
  )
}
